import { defineComponent as _defineComponent } from 'vue';
import BarChart from "./BarChart.vue";
import CarbonEmissionsCard from "./CarbonEmissionCard.vue";
import { computed, unref } from "vue";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Heading from "@/components/Common/Heading.vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import * as carbonEmissionsConstants from "./carbonEmissionConstants.js";
export default /*#__PURE__*/ _defineComponent({
    __name: 'CarbonEmissions',
    props: {
        estimatedServerInstance: null,
        jobRuntimeInSeconds: null,
        coresAllocated: null,
        memoryAllocatedInMebibyte: { default: 0 }
    },
    setup(__props) {
        const props = __props;
        library.add(faQuestionCircle);
        const carbonEmissions = computed(() => {
            const powerUsageEffectiveness = carbonEmissionsConstants.worldwidePowerUsageEffectiveness;
            const memoryPowerUsed = carbonEmissionsConstants.memoryPowerUsage;
            const runtimeInHours = props.jobRuntimeInSeconds / (60 * 60); // Convert to hours
            const memoryAllocatedInGibibyte = props.memoryAllocatedInMebibyte / 1024; // Convert to gibibyte
            const cpuInfo = props.estimatedServerInstance.cpuInfo;
            const tdpPerCore = cpuInfo.tdp / cpuInfo.totalAvailableCores;
            const normalizedTdpPerCore = tdpPerCore * props.coresAllocated;
            // Power needed in Watt
            const powerNeededCpu = powerUsageEffectiveness * normalizedTdpPerCore;
            const powerNeededMemory = powerUsageEffectiveness * memoryAllocatedInGibibyte * memoryPowerUsed;
            const totalPowerNeeded = powerNeededCpu + powerNeededMemory;
            // Energy needed. Convert Watt to kWh
            const energyNeededCPU = (runtimeInHours * powerNeededCpu) / 1000;
            const energyNeededMemory = (runtimeInHours * powerNeededMemory) / 1000;
            const totalEnergyNeeded = (runtimeInHours * totalPowerNeeded) / 1000;
            // Carbon emissions (carbon intensity is in grams/kWh so emissions results are in grams of CO2)
            const carbonIntensity = carbonEmissionsConstants.worldwideCarbonIntensity;
            const cpuCarbonEmissions = energyNeededCPU * carbonIntensity;
            const memoryCarbonEmissions = energyNeededMemory * carbonIntensity;
            const totalCarbonEmissions = totalEnergyNeeded * carbonIntensity;
            return {
                cpuCarbonEmissions,
                memoryCarbonEmissions,
                totalCarbonEmissions,
                energyNeededCPU,
                energyNeededMemory,
                totalEnergyNeeded,
            };
        });
        const carbonEmissionsComparisons = computed(() => {
            if (!carbonEmissions.value) {
                return;
            }
            const { totalCarbonEmissions, totalEnergyNeeded } = carbonEmissions.value;
            const { averagePassengerCarEmissionsEU, averagePassengerCarEmissionsUS, gasolineCarbonEmissionsPerGallon, lightbulbEnergyUsage, smartphonesChargedCarbonEmissions, treeYear, } = carbonEmissionsConstants;
            const gasolineConsumed = {
                heading: "Gasoline consumed",
                explanation: "The amount of gasoline consumed running this job",
                value: prettyPrintValue({
                    // Multiply by 3.785 to convert gallons to litres
                    value: parseFloat((totalCarbonEmissions / (gasolineCarbonEmissionsPerGallon * 3.785)).toFixed(2)),
                    threshold: 1e-1,
                    unit: "l",
                }),
                icon: "gasPump",
            };
            const drivingInEU = {
                heading: "Distance driven in EU",
                explanation: "Distance driven in km in the EU",
                value: prettyPrintValue({
                    value: parseFloat((totalCarbonEmissions / averagePassengerCarEmissionsEU).toFixed(2)),
                    threshold: 1e-1,
                    unit: "km",
                }),
                icon: "car",
            };
            const drivingInUS = {
                heading: "Distance driven in US",
                explanation: "Distance driven in miles in the US",
                value: prettyPrintValue({
                    value: parseFloat((totalCarbonEmissions / averagePassengerCarEmissionsUS).toFixed(2)),
                    threshold: 1e-1,
                    unit: "mile(s)",
                }),
                icon: "car",
            };
            const lightbulbsRunning = {
                heading: "Light bulbs running",
                explanation: "The amount of incandescent light bulbs you could have powered for one hour given your job's energy usage",
                value: prettyPrintValue({
                    value: parseFloat((totalEnergyNeeded / lightbulbEnergyUsage).toFixed(3)),
                    threshold: 1,
                }),
                icon: "lightbulb",
            };
            const smartphonesCharged = {
                heading: "Phones charged",
                explanation: "How many smartphones you could have charged given your job's energy usage",
                value: prettyPrintValue({
                    value: parseFloat((totalCarbonEmissions / (smartphonesChargedCarbonEmissions * 1e6)).toFixed(2)),
                    threshold: 1e-1 * 5, // at least one smartphone charged to 50%
                }),
                icon: "mobilePhone",
            };
            const treeMonths = {
                heading: "Tree months",
                explanation: "How long it would take a tree to sequester the carbon emissions from your job",
                value: prettyPrintValue({
                    value: Math.round((totalCarbonEmissions / treeYear) * 12),
                    threshold: 1e-1 * 5, // at least half of a month
                }),
                icon: "tree",
            };
            return [drivingInEU, drivingInUS, gasolineConsumed, lightbulbsRunning, smartphonesCharged, treeMonths];
        });
        const canShowMemory = computed(() => {
            return props.memoryAllocatedInMebibyte && props.memoryAllocatedInMebibyte !== 0;
        });
        const canShowMoreThanOneMetric = computed(() => {
            return unref(canShowMemory);
        });
        const barChartData = computed(() => {
            var _a;
            const values = carbonEmissions.value;
            if (!values) {
                return;
            }
            const data = [
                {
                    name: "CPU",
                    value: getPercentage(values.cpuCarbonEmissions, values.totalCarbonEmissions),
                },
            ];
            const canShowMemory = ((_a = carbonEmissions.value) === null || _a === void 0 ? void 0 : _a.memoryCarbonEmissions) !== 0;
            if (canShowMemory) {
                data.push({
                    name: "Memory",
                    value: getPercentage(values.memoryCarbonEmissions, values.totalCarbonEmissions),
                });
            }
            return data;
        });
        function prettyPrintValue({ value, threshold, unit, }) {
            if (value < threshold) {
                return `≤ ${threshold}  ${unit !== null && unit !== void 0 ? unit : ""}`;
            }
            return `${value} ${unit !== null && unit !== void 0 ? unit : ""}`;
        }
        function getPercentage(value, total) {
            return Number(((value / total) * 100).toFixed(2));
        }
        function getCarbonEmissionsText(carbonEmissionsInGrams) {
            let adjustedCarbonEmissions = carbonEmissionsInGrams;
            let unitMagnitude = "g";
            if (carbonEmissionsInGrams === 0) {
                return "0 g CO2e";
            }
            if (carbonEmissionsInGrams >= 1e6) {
                adjustedCarbonEmissions /= 1e6;
                unitMagnitude = "t";
            }
            else if (carbonEmissionsInGrams >= 1e3 && carbonEmissionsInGrams < 1e6) {
                adjustedCarbonEmissions /= 1000;
                unitMagnitude = "kg";
            }
            else if (carbonEmissionsInGrams >= 1 && carbonEmissionsInGrams <= 999) {
                unitMagnitude = "g";
            }
            else if (carbonEmissionsInGrams < 1 && carbonEmissionsInGrams > 1e-4) {
                adjustedCarbonEmissions *= 1000;
                unitMagnitude = "mg";
            }
            else {
                adjustedCarbonEmissions *= 1e6;
                unitMagnitude = "μg";
            }
            const roundedValue = Math.round(adjustedCarbonEmissions);
            return `${roundedValue === 0 ? "< 1" : roundedValue} ${unitMagnitude} CO2e`;
        }
        function getEnergyNeededText(energyNeededInKiloWattHours) {
            let adjustedEnergyNeeded = energyNeededInKiloWattHours;
            let unitMagnitude = "kW⋅h";
            if (energyNeededInKiloWattHours === 0) {
                return "0 kW⋅h";
            }
            if (energyNeededInKiloWattHours >= 1e3) {
                adjustedEnergyNeeded /= 1000;
                unitMagnitude = "MW⋅h";
            }
            else if (energyNeededInKiloWattHours >= 1 && energyNeededInKiloWattHours <= 999) {
                unitMagnitude = "W⋅h";
            }
            else if (energyNeededInKiloWattHours < 1 && energyNeededInKiloWattHours > 1e-4) {
                adjustedEnergyNeeded *= 1000;
                unitMagnitude = "mW⋅h";
            }
            else {
                adjustedEnergyNeeded *= 1e6;
                unitMagnitude = "µW⋅h";
            }
            const roundedValue = Math.round(adjustedEnergyNeeded);
            return `${roundedValue === 0 ? "< 1" : roundedValue} ${unitMagnitude}`;
        }
        return { __sfc: true, props, carbonEmissions, carbonEmissionsComparisons, canShowMemory, canShowMoreThanOneMetric, barChartData, prettyPrintValue, getPercentage, getCarbonEmissionsText, getEnergyNeededText, BarChart, CarbonEmissionsCard, FontAwesomeIcon, Heading };
    }
});
